import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { generatePath, useHistory } from "react-router-dom";
import routing from "routing";
import type {
	Folder,
	FolderDetails,
	FolderItem,
	FolderItemType,
} from "src/components/Folders/types";
import newApi from "src/newApi";
import { getService } from "../helpers/isPes";
import options from "./defaultOptions";

// --- Mappers (backend number → frontend string) ---

function mapFolderSearchItem(item: FolderSearchItem): Folder {
	return {
		id: String(item.id),
		name: item.name,
		examCount: item.examCount,
		courseCount: item.courseCount,
		decksCount: item.decksCount,
		notesCount: item.notesCount,
		totalItemsCount: item.totalItemsCount,
		details: item.details ?? null,
	};
}

function mapFolderItemResult(item: FolderItemResult): FolderItem {
	return {
		id: String(item.id),
		data: item.data,
	};
}

function mapFolderDetail(detail: FolderDetailRaw): FolderDetail {
	const items: FolderDetail["items"] = {};
	if (detail.items) {
		for (const [key, value] of Object.entries(detail.items) as [
			FolderItemType,
			FolderItemResult[] | undefined,
		][]) {
			if (value) {
				items[key] = value.map(mapFolderItemResult);
			}
		}
	}
	return {
		id: String(detail.id),
		name: detail.name,
		details: detail.details ?? null,
		items: Object.keys(items).length > 0 ? items : undefined,
	};
}

// --- Search ---

interface FolderSearchParams {
	search?: string;
	take?: number;
	page?: number;
	specId?: number;
	orderBy?: "createdAt" | "updatedAt" | "name";
	order?: "ASC" | "DESC";
}

interface FolderSearchItem {
	id: number;
	name: string;
	userId: number;
	createdAt: string;
	updatedAt: string;
	decksCount: number;
	examCount: number;
	courseCount: number;
	notesCount: number;
	totalItemsCount: number;
	details: FolderDetails | null;
}

interface FolderSearchResult {
	data: FolderSearchItem[];
	totalCount: number;
	page: number;
	hasNextPage: boolean;
}

interface FolderSearchMappedResult {
	data: Folder[];
	totalCount: number;
	page: number;
	hasNextPage: boolean;
}

function mapFolderSearchResult(
	result: FolderSearchResult,
): FolderSearchMappedResult {
	return {
		...result,
		data: result.data.map(mapFolderSearchItem),
	};
}

// --- Folder items ---

interface FolderItemResult {
	id: number;
	name: string | null;
	folderId: number;
	userId: number;
	itemType: FolderItemType;
	itemId: number;
	createdAt: string;
	updatedAt: string;
	data: Record<string, unknown> | null;
}

interface FolderItemsResult {
	data: FolderItemResult[];
	totalCount: number;
	page: number;
	hasNextPage: boolean;
}

interface FolderItemsMappedResult {
	data: FolderItem[];
	totalCount: number;
	page: number;
	hasNextPage: boolean;
}

function mapFolderItemsResult(
	result: FolderItemsResult,
): FolderItemsMappedResult {
	return {
		...result,
		data: result.data.map(mapFolderItemResult),
	};
}

// --- Single folder ---

interface FolderDetailRaw {
	id: number;
	name: string;
	userId: number;
	createdAt: string;
	updatedAt: string;
	details: FolderDetails | null;
	items?: Partial<Record<FolderItemType, FolderItemResult[]>>;
}

interface FolderDetail {
	id: string;
	name: string;
	details: FolderDetails | null;
	items?: Partial<Record<FolderItemType, FolderItem[]>>;
}

// --- Mutation payloads ---

interface AddFolderItemsPayload {
	folderId: string;
	items: { itemId: string; itemType: FolderItemType }[];
}

interface RemoveFolderItemsPayload {
	folderId: string;
	items: number[];
}

interface MoveFolderItemsBody {
	targetFolderId: number;
	examsIds?: number[];
	courseIds?: number[];
	notesIds?: number[];
	decksIds?: number[];
}

interface MoveFolderItemsPayload {
	folderId?: string;
	body: MoveFolderItemsBody;
}

interface CreateFolderPayload {
	name: string;
	details?: FolderDetails | null;
}

interface UpdateFolderPayload {
	id: string;
	name: string;
	details?: FolderDetails | null;
}

interface CreatedFolder {
	id: number;
	name: string;
	userId: number;
	createdAt: string;
	updatedAt: string;
}

// --- User notes ---

interface UserNotesSearchParams {
	take?: number;
	page?: number;
	search?: string;
	[key: string]: unknown;
}

interface UserNotesResult {
	data: Record<string, unknown>[];
	totalCount: number;
	page: number;
	hasNextPage: boolean;
}

// --- Hooks ---

export function useFoldersSearch(params: FolderSearchParams) {
	return useQuery<FolderSearchMappedResult>({
		queryKey: ["folders", getService(), params],

		queryFn: async () => {
			const data = await newApi.searchFolders(params);
			return mapFolderSearchResult(data);
		},

		...options,
	});
}

export function useFoldersSearchInfinite(
	params: FolderSearchParams,
	enabled = true,
) {
	return useInfiniteQuery<FolderSearchMappedResult>({
		queryKey: ["folders-infinite", getService(), params],
		queryFn: async ({ pageParam = 0 }) => {
			const data = await newApi.searchFolders({
				...params,
				page: pageParam,
			});
			return mapFolderSearchResult(data);
		},
		initialPageParam: 0,
		...options,
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.hasNextPage ? allPages.length : undefined;
		},
		enabled: enabled,
	});
}

export function useCreateFolder() {
	const queryClient = useQueryClient();

	return useMutation<CreatedFolder, unknown, CreateFolderPayload>({
		mutationFn: async ({ name, details }) => {
			const data = await newApi.createFolder(name, details);
			return data;
		},

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["folders"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folders-infinite"],
			});
		},
	});
}

export function useUpdateFolder() {
	const queryClient = useQueryClient();

	return useMutation<unknown, unknown, UpdateFolderPayload>({
		mutationFn: async ({ id, name, details }) => {
			const data = await newApi.updateFolder(id, name, details);
			return data;
		},

		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({
				queryKey: ["folders"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folders-infinite"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder", getService(), id],
			});
		},
	});
}

export function useGetFolder(id: string, params?: Record<string, unknown>) {
	const history = useHistory();

	const query = useQuery<FolderDetail>({
		queryKey: ["folder", getService(), id, params],

		queryFn: async () => {
			const data = await newApi.getFolder(id, params);
			return mapFolderDetail(data);
		},

		...options,
		enabled: !!id,
		placeholderData: keepPreviousData,
	});

	// TanStack Query 5: bez onError w useQuery — nieistniejący folder → lista folderów
	useEffect(() => {
		const status = (query.error as { response?: { status?: number } } | null)
			?.response?.status;
		if (status === 404) {
			history.replace(generatePath(routing.paths.foldersList));
		}
	}, [query.error, history]);

	return query;
}

export function useGetFolderItemsInfinite(
	id: string,
	params?: Record<string, unknown>,
	enabled = true,
) {
	return useInfiniteQuery<FolderItemsMappedResult>({
		queryKey: ["folder-items", getService(), id, params],
		queryFn: async ({ pageParam = 0 }) => {
			const data = await newApi.getFolderItemsOffset(id, {
				...params,
				page: pageParam,
			});
			return mapFolderItemsResult(data);
		},
		initialPageParam: 0,
		...options,
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.hasNextPage ? allPages.length : undefined;
		},
		enabled: !!id && enabled,
	});
}

export function useGetUserNote(noteId: string) {
	return useQuery({
		queryKey: ["user-note", getService(), noteId],

		queryFn: async () => {
			const data = await newApi.getUserNote(noteId);
			return data;
		},

		...options,
		enabled: !!noteId,
	});
}

export function useDeleteUserNote() {
	const queryClient = useQueryClient();

	return useMutation<void, unknown, string>({
		mutationFn: async (noteId) => {
			await newApi.deleteUserNote(noteId);
		},

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["folders-infinite"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder-items"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folders"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder"],
			});
			queryClient.invalidateQueries({
				queryKey: ["user-notes-infinite"],
			});
		},
	});
}

export function useDeleteFolder() {
	const queryClient = useQueryClient();

	return useMutation<void, unknown, string>({
		mutationFn: async (id) => {
			await newApi.deleteFolder(id);
		},

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["folders"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folders-infinite"],
			});
		},
	});
}

export function useSearchUserNotesInfinite(
	params: UserNotesSearchParams,
	enabled = true,
) {
	return useInfiniteQuery<UserNotesResult>({
		queryKey: ["user-notes-infinite", getService(), params],
		queryFn: async ({ pageParam = 0 }) => {
			return newApi.searchUserNotes({
				...params,
				page: pageParam,
			});
		},
		initialPageParam: 0,
		...options,
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.hasNextPage ? allPages.length : undefined;
		},
		enabled: enabled,
	});
}

export function useAddFolderItems() {
	const queryClient = useQueryClient();

	return useMutation<void, unknown, AddFolderItemsPayload>({
		mutationFn: async ({ folderId, items }) => {
			await newApi.addFolderItems(folderId, items);
		},

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["folders"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folders-infinite"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder-items"],
			});
		},
	});
}

export function useRemoveFolderItems() {
	const queryClient = useQueryClient();

	return useMutation<void, unknown, RemoveFolderItemsPayload>({
		mutationFn: async ({ folderId, items }) => {
			await newApi.removeFolderItems(folderId, items);
		},

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["folders"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folders-infinite"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder-items"],
			});
		},
	});
}

export function useMoveFolderItems() {
	const queryClient = useQueryClient();

	return useMutation<void, unknown, MoveFolderItemsPayload>({
		mutationFn: async ({ folderId, body }) => {
			await newApi.moveFolderItems(folderId, body);
		},

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["folders"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folders-infinite"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder-items"],
			});
		},
	});
}
